import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, DollarSign, FileText, ArrowRight, TrendingUp, TrendingDown, Wallet, Table2, CheckCircle2, ExternalLink } from 'lucide-react';
import { getStats, getSheetsStatus } from '@/lib/api';
import { toast } from 'sonner';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetsStatus, setSheetsStatus] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    loadData();
    
    // Check for sheets connection success
    if (searchParams.get('sheets_connected') === 'true') {
      toast.success('Google Sheets connected successfully! Your data will now sync automatically.');
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [statsRes, sheetsRes] = await Promise.all([
        getStats(),
        getSheetsStatus().catch(() => ({ data: { connected: false } }))
      ]);
      setStats(statsRes.data);
      setSheetsStatus(sheetsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const connectGoogleSheets = () => {
    // Redirect to Google Sheets OAuth
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backendUrl}/api/oauth/sheets/login?user_id=${user.user_id}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        {/* Main Content */}
        <main className="flex-1 overflow-y-auto pb-24 md:pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
              {/* Welcome Section */}
              <div className="text-center pt-8 pb-4">
                <p className="text-muted-foreground text-sm mb-2">{getGreeting()}</p>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {user?.name?.split(' ')[0]}
                </h1>
                <p className="text-muted-foreground italic text-sm">
                  "Pray - India Shall Be Saved"
                </p>
              </div>

              {/* Financial Overview - Clean Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-white/80 backdrop-blur border-0 shadow-sm">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                      <TrendingUp className="h-6 w-6 text-emerald-600" />
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">Total Income</p>
                    <p className="text-2xl font-bold text-emerald-600" data-testid="stat-income">
                      {formatCurrency(stats?.total_income_inr)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-white/80 backdrop-blur border-0 shadow-sm">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                      <TrendingDown className="h-6 w-6 text-red-500" />
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
                    <p className="text-2xl font-bold text-red-500" data-testid="stat-expenses">
                      {formatCurrency(stats?.total_expense_amount_inr)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-white/80 backdrop-blur border-0 shadow-sm">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      <Wallet className="h-6 w-6 text-primary" />
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">Balance</p>
                    <p className="text-2xl font-bold text-primary" data-testid="stat-balance">
                      {formatCurrency(stats?.net_position_inr)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Stats */}
              <div className="flex justify-center gap-8 py-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-foreground">{stats?.total_events || 0}</p>
                  <p className="text-xs text-muted-foreground">Events</p>
                </div>
                <div className="h-12 w-px bg-border"></div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-foreground">{stats?.total_expenses || 0}</p>
                  <p className="text-xs text-muted-foreground">Transactions</p>
                </div>
                <div className="h-12 w-px bg-border"></div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-amber-600">{stats?.pending_budgets || 0}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>

              {/* Quick Actions - Clean and Simple */}
              <div className="space-y-3 pt-4">
                <h2 className="text-sm font-medium text-muted-foreground text-center mb-4">Quick Actions</h2>
                
                <Link to="/events" data-testid="action-events">
                  <Card className="bg-white hover:bg-slate-50 transition-colors cursor-pointer border-0 shadow-sm mb-3">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">View Events</p>
                          <p className="text-sm text-muted-foreground">Manage your revival meetings</p>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/expenses/new" data-testid="action-expense">
                  <Card className="bg-white hover:bg-slate-50 transition-colors cursor-pointer border-0 shadow-sm mb-3">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Log Expense</p>
                          <p className="text-sm text-muted-foreground">Record a new transaction</p>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/income" data-testid="action-income">
                  <Card className="bg-white hover:bg-slate-50 transition-colors cursor-pointer border-0 shadow-sm mb-3">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Income & Transfers</p>
                          <p className="text-sm text-muted-foreground">Track donations and allocations</p>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </CardContent>
                  </Card>
                </Link>
              </div>

              {/* Admin Notice */}
              {user?.role === 'admin' && (
                <div className="text-center pt-4">
                  <p className="text-xs text-muted-foreground">
                    You have admin access · Viewing all events and data
                  </p>
                </div>
              )}

              {/* Google Sheets Integration Card */}
              <Card className={`${sheetsStatus?.connected ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'} border shadow-sm`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${sheetsStatus?.connected ? 'bg-emerald-100' : 'bg-blue-100'} flex items-center justify-center`}>
                        <Table2 className={`h-5 w-5 ${sheetsStatus?.connected ? 'text-emerald-600' : 'text-blue-600'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground">Google Sheets</p>
                          {sheetsStatus?.connected && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {sheetsStatus?.connected 
                            ? 'Auto-syncing all your data' 
                            : 'Connect to sync data automatically'}
                        </p>
                      </div>
                    </div>
                    {sheetsStatus?.connected ? (
                      <a 
                        href="https://docs.google.com/spreadsheets/d/1r-92WtzTDtDtBy-lvhWbwLeWODzk-A2dPZplZouhYL4/edit"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700"
                      >
                        Open Sheet <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <Button 
                        onClick={connectGoogleSheets}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="connect-sheets-btn"
                      >
                        Connect
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>

        <MobileNav user={user} />
      </div>
    </div>
  );
};

export default Dashboard;
